import { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../lib/api';
import { PIPELINE_STAGES, STATUS_LABELS, fmtCurrency, PARTNERSHIP_TYPES, PARTNERSHIP_TYPE_LABELS } from '../lib/utils';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TYPE_BADGE = {
  media_partnership: 'bg-magenta/10 text-magenta',
  strategic_partnership: 'bg-navy/10 text-navy',
  sponsorship: 'bg-amber-100 text-amber-700',
  marketplace_partner: 'bg-cyan-100 text-cyan-700',
  workforce_partner: 'bg-emerald-100 text-emerald-700',
  gala_partner: 'bg-purple-100 text-purple-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  const load = () => api.get('/leads').then((r) => setLeads(r.data));
  useEffect(() => {
    load();
    api.get('/users').then((r) => setUsers(r.data));
  }, []);

  const filtered = leads.filter((l) => {
    if (typeFilter !== 'all' && l.partnership_type !== typeFilter) return false;
    if (assigneeFilter !== 'all' && l.assigned_to !== assigneeFilter) return false;
    return true;
  });

  const byStage = (stage) => filtered.filter((l) => l.status === stage);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (destination.droppableId === source.droppableId) return;

    const newStatus = destination.droppableId;
    setLeads((prev) => prev.map((l) => (l.id === draggableId ? { ...l, status: newStatus } : l)));

    const form = new FormData();
    form.append('status_value', newStatus);
    try {
      await api.patch(`/leads/${draggableId}/status`, form);
      toast.success(`Moved to ${STATUS_LABELS[newStatus]}`);
    } catch (e) {
      toast.error('Failed to update');
      load();
    }
  };

  const stats = {
    pipelineValue: filtered.filter((l) => !['closed_won', 'closed_lost', 'inactive', 'future_opportunity'].includes(l.status)).reduce((s, l) => s + (l.estimated_revenue || 0), 0),
    closedWon: filtered.filter((l) => l.status === 'closed_won').length,
    closedLost: filtered.filter((l) => l.status === 'closed_lost').length,
    avgDeal: 0,
  };
  const wonLeads = filtered.filter((l) => l.status === 'closed_won');
  stats.avgDeal = wonLeads.length ? wonLeads.reduce((s, l) => s + (l.revenue || 0), 0) / wonLeads.length : 0;
  stats.closeRate = (stats.closedWon + stats.closedLost) ? Math.round((stats.closedWon / (stats.closedWon + stats.closedLost)) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-56" data-testid="pipeline-type-filter">
            <SelectValue placeholder="Partnership Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Partnership Types</SelectItem>
            {PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-56" data-testid="pipeline-assignee-filter">
            <SelectValue placeholder="Team Member" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team Members</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Pipeline Value" value={fmtCurrency(stats.pipelineValue)} testid="stat-pipeline-value" />
        <Stat label="Average Deal Size" value={fmtCurrency(stats.avgDeal)} testid="stat-avg-deal" />
        <Stat label="Close Rate" value={`${stats.closeRate}%`} testid="stat-close-rate" />
        <Stat label="Total Won" value={stats.closedWon} testid="stat-total-won" />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
          {PIPELINE_STAGES.map((stage) => {
            const list = byStage(stage);
            const total = list.reduce((s, l) => s + (l.estimated_revenue || 0), 0);
            return (
              <Droppable key={stage} droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`bg-slate-50 rounded-xl p-3 w-80 shrink-0 border-2 transition-colors ${snapshot.isDraggingOver ? 'border-magenta border-dashed bg-magenta/5' : 'border-transparent'}`}
                    data-testid={`stage-${stage}`}
                  >
                    <div className="flex items-center justify-between px-2 mb-3">
                      <div>
                        <div className="font-heading font-bold text-sm text-navy">{STATUS_LABELS[stage]}</div>
                        <div className="text-xs text-slate-500">{list.length} · {fmtCurrency(total)}</div>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${stage === 'closed_won' ? 'bg-emerald-500' : stage === 'closed_lost' ? 'bg-rose-500' : 'bg-magenta'}`} />
                    </div>
                    <div className="space-y-2 min-h-[100px]">
                      {list.map((lead, idx) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={idx}>
                          {(p, snap) => (
                            <div
                              ref={p.innerRef}
                              {...p.draggableProps}
                              {...p.dragHandleProps}
                              className={`bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:border-magenta hover:shadow-md transition cursor-grab active:cursor-grabbing ${snap.isDragging ? 'kanban-card-dragging' : ''}`}
                              data-testid={`pipeline-card-${lead.id}`}
                            >
                              <div className="font-semibold text-sm text-navy truncate">{lead.company_name}</div>
                              <div className="text-xs text-slate-500 truncate mt-0.5">{lead.primary_contact}</div>
                              <span className={`inline-block mt-2 text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${TYPE_BADGE[lead.partnership_type] || TYPE_BADGE.other}`}>
                                {PARTNERSHIP_TYPE_LABELS[lead.partnership_type]?.replace(' Partnership', '').replace(' Partner', '')}
                              </span>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs font-semibold text-magenta">{fmtCurrency(lead.estimated_revenue)}</span>
                                <span className="text-[10px] text-slate-400 truncate ml-2">{lead.partnership_tier}</span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={testid}>
      <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-bold text-navy mt-1">{value}</div>
    </div>
  );
}
